import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Warning, Download } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * In-app "Delete My Account" + "Download My Data" card.
 * Required for Google Play Store + DPDP Act 2023 compliance.
 * Shows a confirmation modal before destructive deletion.
 */
const DeleteAccountSection = () => {
  const { user, logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/me`, { withCredentials: true });
      toast.success('Your account has been deleted. Thank you for using BeeWeddy.');
      // Brief delay so the toast renders before redirect
      setTimeout(() => {
        logout();
      }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/data-export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `beeweddi-data-${user?.phone || 'me'}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Your data has been downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Admin accounts cannot self-delete
  if (user?.role === 'admin') return null;

  return (
    <Card className="bg-white border-red-200 mt-6" data-testid="delete-account-section">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2 text-red-700">
          <Trash size={20} className="text-red-600" /> Privacy & Account Controls
        </CardTitle>
        <CardDescription>
          Download a copy of your data, or permanently delete your BeeWeddy account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          onClick={handleExport}
          disabled={exporting}
          variant="outline"
          className="rounded-none border-[#E8E1D5] w-full sm:w-auto"
          data-testid="export-data-btn"
        >
          <Download size={16} className="mr-2" />
          {exporting ? 'Preparing download...' : 'Download My Data (JSON)'}
        </Button>

        <div className="border-t border-[#E8E1D5] pt-4">
          <div className="flex items-start gap-2 mb-3 text-sm text-text-secondary">
            <Warning size={18} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
            <span>
              Deleting your account permanently removes your profile, bookings, wallet balance, services and all uploaded
              photos. This cannot be undone.
            </span>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-none w-full sm:w-auto"
            data-testid="delete-account-btn"
          >
            <Trash size={16} className="mr-2" /> Delete My Account
          </Button>
        </div>
      </CardContent>

      {showModal && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !deleting) setShowModal(false); }}
          data-testid="delete-account-modal"
        >
          <div className="bg-white max-w-md w-full border border-red-300 shadow-2xl rounded">
            <div className="px-6 py-4 border-b border-[#E8E1D5] flex items-center gap-2">
              <Trash size={22} className="text-red-600" />
              <h3 className="font-heading text-xl font-medium text-red-700">Delete Account</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-text-primary">
                You are about to permanently delete your BeeWeddy account. This action <strong>cannot be undone</strong>.
              </p>
              <div className="text-xs text-text-secondary border border-[#E8E1D5] rounded p-3 bg-sand-50">
                <strong className="text-text-primary block mb-1">What will be deleted:</strong>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Profile, photos, business listings</li>
                  <li>Bookings, saved packages, package submissions</li>
                  <li>Wallet balance and pending withdrawals</li>
                  <li>Referral records (you will no longer earn from past invites)</li>
                  <li>Galleries, services, captain assignments</li>
                </ul>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                  Type <span className="font-mono text-red-600">DELETE</span> to confirm
                </Label>
                <Input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="rounded-none border-[#E8E1D5] mt-1 font-mono"
                  data-testid="delete-confirm-text"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleDelete}
                  disabled={confirmText !== 'DELETE' || deleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-delete-account-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete Forever'}
                </Button>
                <Button
                  onClick={() => { setConfirmText(''); setShowModal(false); }}
                  disabled={deleting}
                  variant="outline"
                  className="rounded-none border-[#E8E1D5]"
                  data-testid="cancel-delete-account-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default DeleteAccountSection;
